import { useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { clientClickEvent } from 'utils/custom-cashier-events/client-click-event';
import { changePanoramicStep } from '../state/panoramic-billing-page-actions';

export const PANORAMIC_PAYMENT_STEP = 1;
export const PANORAMIC_BILLING_STEP = 2;

interface UsePanoramicStepNavigationResult {
  step: number;
  isBillingStep: boolean;
  goToBilling: () => void;
  goToPayment: () => void;
  resetStep: () => void;
}

export const usePanoramicStepNavigation = (
  isTwoPageFlow: boolean,
): UsePanoramicStepNavigationResult => {
  const dispatch = useDispatch();
  const step = useSelector(
    (state: any) => state.panoramicBillingPageStep ?? PANORAMIC_PAYMENT_STEP,
  );
  const gatewayId = useSelector((state: any) => state.currentGateway?.option_id ?? null);
  const method = useSelector((state: any) => state.gatewayInfo?.method);
  const separatePageFlag = useSelector(
    (state: any) =>
      state.initTheme?.theme_editor_settings?.customization_settings
        ?.billing_information_separate_page === true,
  );

  const goToBilling = useCallback(() => {
    dispatch(changePanoramicStep(PANORAMIC_BILLING_STEP));
  }, [dispatch]);

  const goToPayment = useCallback(() => {
    clientClickEvent('back_button');
    dispatch(changePanoramicStep(PANORAMIC_PAYMENT_STEP));
  }, [dispatch]);

  const resetStep = useCallback(() => {
    dispatch(changePanoramicStep(PANORAMIC_PAYMENT_STEP));
  }, [dispatch]);

  const prevKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const key = `${gatewayId}-${method}`;
    if (prevKeyRef.current === null) {
      prevKeyRef.current = key;
      return;
    }
    if (prevKeyRef.current !== key) {
      prevKeyRef.current = key;
      if (step !== PANORAMIC_PAYMENT_STEP) {
        dispatch(changePanoramicStep(PANORAMIC_PAYMENT_STEP));
      }
    }
  }, [gatewayId, method, step, dispatch]);

  useEffect(() => {
    if (!isTwoPageFlow && step !== PANORAMIC_PAYMENT_STEP) {
      if (separatePageFlag && step === PANORAMIC_BILLING_STEP) {
        return;
      }
      dispatch(changePanoramicStep(PANORAMIC_PAYMENT_STEP));
    }
  }, [isTwoPageFlow, separatePageFlag, step, dispatch]);

  return {
    step,
    isBillingStep: step === PANORAMIC_BILLING_STEP,
    goToBilling,
    goToPayment,
    resetStep,
  };
};
